import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "lib/utils";
import { Clock } from "lucide-react";
import type { LocationPros } from "../../types/baseTypes";
import { useSelector } from "react-redux";
import type { RootState } from "../../redux/store";
import { formatOpeningHour, shouldShowOpeningHours } from "../../modules/bookLocation/common/locationSelection/visibility";

/**
 * Opening Hours Row Interface
 * Represents a single day's opening hours
 */
interface OpeningHourRow {
  weekday: string;
  fromTime: string;
  toTime: string;
  hasHours: boolean;
}

/**
 * OpeningHours Component Props
 */
interface OpeningHoursProps {
  /** Location object containing opening hours data */
  location: LocationPros | null;
  /** Optional CSS class name for styling */
  className?: string;
  /** Show icon next to title */
  showIcon?: boolean;
  /** Compact mode - removes spacing and padding */
  compact?: boolean;
  /** Use booking flow config instead of locations page config */
  useBookingFlowConfig?: boolean;
}

/**
 * Weekdays ordered Monday through Sunday
 */
const WEEKDAYS_ORDERED = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

/**
 * Get time format string based on language
 * - de: 24-hour format without leading zero (8:00, 18:00)
 * - en: 12-hour format with AM/PM (8:00 AM, 6:00 PM)
 * - nb: 24-hour format with leading zero (08:00, 18:00)
 */
function getTimeFormat(language: string): string {
  switch (language) {
    case 'de':
      return 'H:mm';
    case 'en':
      return 'h:mm A';
    case 'nb':
      return 'HH:mm';
    default:
      return 'H:mm';
  }
}

/**
 * Format opening hours for display
 */
function formatOpeningHours(
  location: LocationPros | null,
  timeFormat: string
): OpeningHourRow[] {
  if (!location?.openingHours) {
    return [];
  }

  return WEEKDAYS_ORDERED.map((weekday) => {
    const dayEntry = location.openingHours.find((h) => h.weekday === weekday);
    const fromHour = dayEntry?.times?.fromHour;
    const toHour = dayEntry?.times?.toHour;
    
    // Check if day is closed (missing data or 00:00-00:00)
    const isClosed = 
      fromHour === undefined || 
      toHour === undefined || 
      (fromHour === 0 && toHour === 0);

    if (isClosed) {
      return { weekday, fromTime: '', toTime: '', hasHours: false };
    }

    return {
      weekday,
      fromTime: formatOpeningHour(fromHour, timeFormat),
      toTime: formatOpeningHour(toHour, timeFormat),
      hasHours: true,
    };
  });
}

/**
 * OpeningHours Component
 * ---------------------------------------------------------------
 * Reusable component to display location opening hours.
 * 
 * @remarks
 * This component:
 * - Automatically determines if opening hours should be shown based on services
 * - Displays hours in Monday-Sunday order
 * - Handles missing or invalid hours gracefully
 * - Uses ShadCN/UI styling with Tailwind CSS
 * - Supports compact mode for inline display
 * 
 * @example
 * ```tsx
 * <OpeningHours location={selectedLocation} />
 * <OpeningHours location={selectedLocation} compact showIcon />
 * ```
 */
export function OpeningHours({
  location,
  className,
  showIcon = false,
  compact = false,
  useBookingFlowConfig = false,
}: OpeningHoursProps) {
  const { t, i18n } = useTranslation();
  const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);

  // Determine if opening hours should be shown based on config
  const configAllowsDisplay = useBookingFlowConfig
    ? (tenantConfig?.showOpeningHoursInBookingFlow ?? true)
    : (tenantConfig?.showOpeningHoursOnLocationsPage ?? true);

  const showOpeningHours = 
    configAllowsDisplay &&
    shouldShowOpeningHours(location);

  // Memoize formatted opening hours to avoid recalculation on every render
  const orderedOpeningHours = useMemo(
    () => formatOpeningHours(location, getTimeFormat(i18n.language)),
    [location, i18n.language]
  );

  // Early returns for cases where we shouldn't display anything
  if (!showOpeningHours) {
    return null;
  }

  if (orderedOpeningHours.every((oh) => !oh.hasHours)) {
    return null;
  }

  // Determine if we should add "Uhr" suffix (for German language)
  const shouldAddUhrSuffix = i18n.language === 'de';

  return (
    <div className={cn(compact ? "space-y-1" : "space-y-2", className)}>
      <div className={cn("flex items-center gap-2", compact ? "text-sm" : "text-base")}>
        {showIcon && <Clock className={cn("text-slate-600", compact ? "h-4 w-4" : "h-5 w-5")} />}
        <p className={cn("font-medium text-slate-900")}>
          {t("location_from_bottom_bar.opening_hours")}
        </p>
      </div>
      <div className={cn("flex flex-col", compact ? "gap-0.5" : "gap-1")}>
        {orderedOpeningHours.map((oh) => (
          <div key={oh.weekday} className="flex items-center">
            <div
              className={cn(
                "font-semibold text-slate-700",
                compact ? "w-20 text-xs" : "w-28 text-sm"
              )}
            >
              {t(`common.weekdays_full.${oh.weekday}`)}
            </div>
            <div className={cn("flex-1 text-slate-600", compact ? "text-xs" : "text-sm")}>
              {oh.hasHours ? (
                <>
                  {oh.fromTime}
                  <span className="mx-1">-</span>
                  {oh.toTime}
                  {shouldAddUhrSuffix && " Uhr"}
                </>
              ) : (
                <span className="italic">{t("location_from_bottom_bar.closed")}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
